import React, { useState } from 'react';
import { Box, makeStyles, SvgIcon, Typography } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import cx from 'classnames';

import { ReactComponent as Bage } from 'img/badge.svg';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from '../../messages';
import { formatPrice } from 'common/utils';
import CheckInBlock from '../CheckInBlock/CheckInBlock';
import inputLabelsMessages from 'common/messages/inputLabels';
import commonMessages from 'common/messages/messages';
import tableHeaders from 'common/messages/tableHeaders';
import BalanceSummaryModal from './BalanceSummaryModal/BalanceSummaryModal';
import { IBalanceSummary } from '../../interfaces';
import { getBalanceValue } from '../../utils';

const useStyles = makeStyles((theme: Theme) => ({
  block: {
    height: 'unset',
    marginTop: 17,
  },
  balanceItem: {
    margin: '8px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  balanceItemValue: {
    fontSize: 14,
    fontWeight: 700,
  },
  reward: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.warning.main,
    '&>svg ': {
      paddingRight: 4,
    },
  },
}));

interface IBalanceProps {
  balance: string;
  reward: number;
  pastDuse: number;
  totalBalance: number;
  showBalanceForMember?: boolean;
  getBalanceSummary: () => void;
  balanceSummary?: IBalanceSummary;
}

const Balance: React.FC<IBalanceProps> = React.memo(
  ({
    balance,
    reward,
    showBalanceForMember,
    pastDuse,
    getBalanceSummary,
    balanceSummary,
  }: IBalanceProps) => {
    const classes = useStyles();
    const intl = useIntl();

    const [isModalOpen, setIsModalOpen] = useState(false);

    const calculateTotalBalance = ((): number => {
      if (showBalanceForMember) {
        return Number(balance) - Number(pastDuse);
      } else {
        return Number(`-${balance}`) + Number(pastDuse);
      }
    })();

    return (
      <>
        <CheckInBlock
          headerDivider
          title={intl.formatMessage(inputLabelsMessages.balance)}
          buttonTitle={intl.formatMessage(commonMessages.viewTitle)}
          onClickButton={() => setIsModalOpen(true)}
          className={classes.block}
        >
          <Box className={classes.balanceItem}>
            <Typography variant="body2" color="textSecondary">
              <FormattedMessage {...tableHeaders.reward} />
            </Typography>
            <Typography component="span" className={cx(classes.balanceItemValue, classes.reward)}>
              <SvgIcon style={{ width: 'auto', height: 17 }}>
                <Bage />
              </SvgIcon>
              {reward || '0'}
            </Typography>
          </Box>
          <Box className={classes.balanceItem}>
            <Typography variant="body2" color="textSecondary">
              <FormattedMessage {...messages.accountBalance} />
            </Typography>
            <Typography component="span" className={classes.balanceItemValue}>
              {getBalanceValue(balance, showBalanceForMember)}
            </Typography>
          </Box>
          <Box className={classes.balanceItem}>
            <Typography variant="body2" color="textSecondary">
              <FormattedMessage {...messages.pastDues} />
            </Typography>
            <Typography component="span" color="error" className={classes.balanceItemValue}>
              {formatPrice(pastDuse)}
            </Typography>
          </Box>
          <Box className={classes.balanceItem}>
            <Typography variant="body2" color="textSecondary">
              <FormattedMessage {...messages.totalBalance} />
            </Typography>
            <Typography component="span" className={classes.balanceItemValue}>
              {formatPrice(calculateTotalBalance)}
            </Typography>
          </Box>
        </CheckInBlock>

        {isModalOpen && (
          <BalanceSummaryModal
            onClose={() => setIsModalOpen(false)}
            getBalanceSummary={getBalanceSummary}
            balanceSummary={balanceSummary}
            showBalanceForMember={showBalanceForMember}
          />
        )}
      </>
    );
  },
);

export default Balance;
